<template>
    <div class="dpo-create-or-edit">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isDpoSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-dpo-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                        <!-- Entity Information -->
                        <fieldset class="p-2 mb-2" style="border:1px solid #D8D6DE;">
                            <legend visible="true" style="width:auto; margin-bottom: 0px; font-size: 14px; font-weight: 400; padding:0 5px; color:#5E5873;"> 
                                <h4> Entity Information </h4>
                            </legend>

                            <validation-provider
                                v-slot="{ errors }"
                                name="company web address"
                            >
                                <b-form-group label="Company web address" label-for="company-web-address">
                                    <b-form-input
                                        id="company-web-address"
                                        v-model="dpoLocal.companyUrl"
                                        trim
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Enter company web address"
                                        :disabled="isDisabled"
                                        data-test ="new-company-web-address"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="company mainline"
                                rules="required"
                            >
                                <b-form-group label="Company mainline" label-for="company-main-line">
                                    <b-form-input
                                        id="company-mainline"
                                        v-model="dpoLocal.companyMainline"
                                        trim
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Enter company mainline"
                                        :disabled="isDisabled"
                                        data-test ="new-company-mainline"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </fieldset>

                        <fieldset class="p-2 mb-2" style="border:1px solid #D8D6DE;">
                            <legend visible="true" style="width:auto; margin-bottom: 0px; font-size: 14px; font-weight: 400; padding:0 5px; color:#5E5873;"> 
                                <h4> Data Protection Officer Details </h4>
                            </legend>

                            <validation-provider
                                v-slot="{ errors }"
                                name="name"
                                rules="required"
                            >
                                <b-form-group label="Name" label-for="name">
                                    <b-form-input
                                        id="name"
                                        v-model="dpoLocal.name"
                                        trim
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Enter name"
                                        :disabled="isDisabled"
                                        data-test ="name"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="email address"
                                rules="required|email"
                            >
                                <b-form-group label="Email address">
                                    <b-form-input
                                        v-model="dpoLocal.email"
                                        placeholder="Enter email address"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled = "isEmailDisabled"
                                        data-test="email"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="designation"
                                rules="required"
                            >
                                <b-form-group label="Designation" label-for="designation">
                                    <b-form-input
                                        id="designation"
                                        v-model="dpoLocal.designation"
                                        trim
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Enter designation"
                                        :disabled="isDisabled"
                                        data-test ="designation"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="office number"
                                rules="required"
                            >
                                <b-form-group label="Office number" label-for="office-number">
                                    <b-form-input
                                        id="office-number"
                                        v-model="dpoLocal.officeNumber"
                                        trim
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Enter office number"
                                        :disabled="isDisabled"
                                        data-test ="office-number"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="appointment date"
                                rules="required"
                            >
                                <b-form-group label="Appointment date" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="dpoLocal.appointmentDate"
                                        placeholder="Enter appointment date"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        :style="dynamicStyle"
                                        :disabled = "isDisabled"
                                        data-test="appointment-date"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                name="cessation date"
                            >
                                <b-form-group label="Cessation date">
                                    <flat-pickr
                                        v-model="dpoLocal.cessationDate"
                                        placeholder="Enter cessation date"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        :style="dynamicStyle"
                                        :disabled = "isDisabled"
                                        data-test="cessation-date"
                                    />
                                </b-form-group>
                            </validation-provider>
                        </fieldset>

                        <!-- Form Actions -->
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                type="submit"
                                data-test="create-dpo-btn"
                                >
                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    {{ dataStatus === 'edit' ? "Update" : "Add " }}
                                </span>

                                <img
                                    v-if="dataStatus === 'create' && !dataSidebarActionLoading"
                                    :src="'/illustrations/add-circle.svg'"
                                    class="ml-1"
                                    alt="add-logo"
                                />

                                <img
                                    v-if="dataStatus === 'edit' && !dataSidebarActionLoading"
                                    :src="'/illustrations/edit.svg'"
                                    class="ml-1"
                                    alt="edit-logo"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-dpo-sidebar-active', false)"
                                >
                                Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { NO_MATCHING_OPTIONS } from "@/constants/errors";

export default {
    components: {
        // BSV
        BForm,
        BButton,
        BSidebar,
        flatPickr,
        BFormGroup,
        BFormInput,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isDpoSidebarActive",
        event: "update:is-dpo-sidebar-active",
    },

    props: {
        isDpoSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataDpo: {
            type: Object,
            required: true,
        },

        dataStatus: {
            type: String,
            default: "view"
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            required,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            NO_MATCHING_OPTIONS,
        };
    },

    computed: {
        isDisabled() {
            if(this.dataStatus === "view")
                return true;
            return false;
        },

        isEmailDisabled() {
            if(this.dataStatus === "view" || this.dataStatus !== 'create')
                return true;
            return false;
        },

        sideBarTitle() {
            if(this.dataStatus === "create")
                return "Add Data Protection Officer";

            if(this.dataStatus === "view")
                return "Data Protection Officer Details";

            return "Update Data Protection Officer";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },
    },
    
    setup(props, { emit }) {
        // eslint-disable-next-line no-shadow
        function useDpoHandler(props, emit) {
            const dpoLocal = ref(JSON.parse(JSON.stringify(props.dataDpo.value)));

            const resetDpoLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                dpoLocal.value = JSON.parse(JSON.stringify(props.dataDpo.value));
            };

            watch(props.dataDpo, () => {
                resetDpoLocal();
            });

            const onSubmit = () => {
                const dataProtectionOfficerData = JSON.parse(JSON.stringify(dpoLocal));

                if (props.dataDpo.value.id) emit('update-dpo', dataProtectionOfficerData.value);
                else emit('add-dpo', dataProtectionOfficerData.value);
            };

            return {
                dpoLocal,

                // UI
                onSubmit,
                resetDpoLocal,
            };
        };

        const {
            dpoLocal,

            // UI
            onSubmit,
            resetDpoLocal
        } = useDpoHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(dpoLocal);

        function clearFormData() {
            resetDpoLocal();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            dpoLocal,

            // Form Validation
            resetForm,
            resetDpoLocal,
            clearForm,
            refFormObserver,
            getValidationState,

            // Methods
            clearFormData,
        };
    },
};
</script>

<style scope>
.dpo-create-or-edit .preloader {
    width: 15px;
    height: 15px;
}

.dpo-create-or-edit .preloader div:before {
    background: var(--primary-color);
}

.dpo-create-or-edit .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 }

/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>
